import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Tile } from 'src/app/core/models/tile';
import { BulkTileDeletionResult } from 'src/app/core/models/bulk-tile-deletion-result';
import { TileRepository } from '@/core/repositories/tile/tile.repository';
import { TtlCacheStore } from '@/core/utils/cache/ttl-cache-store';
import { tap } from 'rxjs/operators';
import { TILE_CACHE_TTL } from '@/core/config/cache-ttl';

@Injectable({ providedIn: 'root' })
export class TileService {
    private readonly tileStore = new TtlCacheStore<Tile[]>('tiles', [], TILE_CACHE_TTL, () =>
        this.tileRepository.getAllTiles(),
    );
    public tiles$ = this.tileStore.state$;

    constructor(private tileRepository: TileRepository) {}

    public loadTiles(forceLoad: boolean = false): Observable<Tile[]> {
        return this.tileStore.load(forceLoad);
    }

    public invalidateCache(): void {
        this.tileStore.invalidate();
    }

    /**
     * Async alternativ for getting the users tiles with async await.
     * It delivers the same result as loadTiles() but returns a Promise.
     * Calling this method is in most cases fast as the tiles are cached.
     * @returns Promise<Tile[]> - Tiles that the current user can see (active and archived)
     */
    public async getTiles(): Promise<Tile[]> {
        return await this.tileStore.getAsync();
    }

    public checkTilesHaveActivities(tileIds: number[]): Observable<boolean> {
        return this.tileRepository.checkTilesHaveActivities(tileIds);
    }

    public deleteTileById(id: number | undefined): Observable<void> {
        return this.tileRepository.deleteTileById(id);
    }

    // bulk tile deletion
    public bulkTileDeletion(tileIds: number[]): Observable<BulkTileDeletionResult> {
        return this.tileRepository.bulkTileDeletion(tileIds);
    }

    /**
     * Only informational - unused, intended for app usage
     * @deprecated Use $tiles or getTiles() instead
     */
    public getAssignedTiles(): Observable<Tile[]> {
        return this.tileRepository.getAssignedTiles();
    }

    /**
     * Only informational - unused, intended for app usage
     * @deprecated Use $tiles or getTiles() instead
     */
    public getTilesForAppTracking(): Observable<Tile[]> {
        return this.getAssignedTiles();
    }

    public create(tile: Tile, assignedUserIds?: number[], assignedGroupIds?: number[]): Observable<Tile> {
        return this.tileRepository
            .create(tile, assignedUserIds, assignedGroupIds)
            .pipe(tap(() => this.updateTileCache()));
    }

    public update(tile: Tile): Observable<Tile> {
        return this.tileRepository.update(tile).pipe(tap(() => this.updateTileCache()));
    }

    public clearCache(): void {
        this.tileStore.reset();
    }

    private updateTileCache(): void {
        this.tileStore.load(true);
    }
}
